/* eslint-disable camelcase */
import { lazy, Suspense } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, ThumbsDown, ThumbsUp } from 'lucide-react'

import { BriefEvidence } from './EvidenceTables'
import FeedbackError from './FeedbackError'
import { usePrereadReport } from './usePrereadFeedback'

import styles from './PrereadFeedback.module.scss'

import { isApiError } from '@/api'
import { readListReturn } from '@/pages/app/SummaryFeedback/listLocation'
import { normalizeFeedbackId } from '@/pages/app/SummaryFeedback/useSummaryFeedback'
import { selectIsInternalAdmin } from '@/store/features/user/userSlice'
import { formatUtcDateTime } from '@/utils/dates'

const BriefContent = lazy(() => import('./BriefContent'))

const notices = {
  consent_required: 'Brief content is withheld because the owner has not enabled data sharing.',
  generation_unavailable:
    'The linked brief is no longer available. It may have been deleted or removed after a connector was disconnected.',
  unmatched: 'This report has no verified match to a generated brief. Its content and tool usage are unavailable.',
  ambiguous: 'Several generations match this report. A specific brief and its tool usage cannot be identified.',
}
const PrereadFeedbackPage = () => {
  const id = normalizeFeedbackId(useParams().id)
  const isAdmin = useSelector(selectIsInternalAdmin)
  const query = usePrereadReport(id, isAdmin)
  const location = useLocation()
  const returnTo = `/app/preread-feedback${readListReturn(location.state)?.search ?? ''}`
  const status = isApiError(query.error) ? query.error.status : undefined
  const detail = query.data
  return (
    <main className={styles.page}>
      <Helmet>
        <title>Preread report · Wispr Flow</title>
      </Helmet>
      <div className={styles.content}>
        <Link className={styles.back} to={returnTo}>
          <ArrowLeft size={16} aria-hidden="true" />
          Preread feedback
        </Link>
        {!isAdmin || status === 403 ? (
          <p role="alert">Internal admin access is required.</p>
        ) : !id || status === 404 ? (
          <p role="alert">Preread feedback not found.</p>
        ) : query.isError ? (
          <FeedbackError detail onRetry={() => void query.refetch()} />
        ) : query.isPending ? (
          <p role="status">Loading preread report…</p>
        ) : (
          detail && (
            <>
              <header className={styles.header}>
                <p className={styles.eyebrow}>
                  {detail.source === 'internal' ? 'Internal' : 'External'} preread report
                </p>
                <h1 className={styles.rating}>
                  {detail.rating === 'up' ? <ThumbsUp aria-hidden="true" /> : <ThumbsDown aria-hidden="true" />}
                  {detail.rating === 'up' ? 'Thumbs up' : 'Thumbs down'}
                </h1>
              </header>
              <section className={styles.panel}>
                <h2>Feedback</h2>
                <blockquote className={styles.feedback}>{detail.feedback?.trim() ? detail.feedback : '-'}</blockquote>
                <dl className={styles.facts}>
                  <div>
                    <dt>Reporter</dt>
                    <dd>{detail.reporterEmail ?? 'Unknown'}</dd>
                  </div>
                  <div>
                    <dt>Reported</dt>
                    <dd>
                      <time dateTime={detail.reportedAt} className={styles.timestamp}>
                        {formatUtcDateTime(detail.reportedAt)}
                      </time>
                    </dd>
                  </div>
                  <div>
                    <dt>Meeting date</dt>
                    <dd>
                      {detail.meetingAt ? (
                        <time dateTime={detail.meetingAt} className={styles.timestamp}>
                          {formatUtcDateTime(detail.meetingAt)}
                        </time>
                      ) : (
                        'Unknown'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Calendar event</dt>
                    <dd>
                      <code>{detail.externalId}</code>
                    </dd>
                  </div>
                  <div>
                    <dt>Generation</dt>
                    <dd>
                      <code>{detail.generationId ?? 'No verified match'}</code>
                    </dd>
                  </div>
                </dl>
                {detail.slackUrl && (
                  <a className={styles.back} href={detail.slackUrl} target="_blank" rel="noopener noreferrer">
                    Open Slack report
                    <ExternalLink size={14} aria-hidden="true" />
                  </a>
                )}
              </section>
              <section className={styles.panel}>
                <h2>Reported brief</h2>
                {detail.contentWithheldReason ? (
                  <p className={styles.notice}>{notices[detail.contentWithheldReason]}</p>
                ) : (
                  <>
                    <h3>{detail.briefTitle ?? 'Meeting brief'}</h3>
                    {detail.briefContent !== null ? (
                      <Suspense fallback={<p role="status">Loading brief…</p>}>
                        <BriefContent content={detail.briefContent} />
                      </Suspense>
                    ) : (
                      <p>Brief content is unavailable.</p>
                    )}
                  </>
                )}
              </section>
              <section className={styles.panel}>
                <h2>Evidence and tool usage</h2>
                <BriefEvidence evidence={detail.evidenceMetadata} />
              </section>
            </>
          )
        )}
      </div>
    </main>
  )
}

export default PrereadFeedbackPage
